import { useMemo } from "react";
import { levenshtein } from "../../../hooks/utils";
import { Aircraft } from "../../../types";

export interface FilteredAircraft {
  option: Aircraft;
  score: number; // Lower score = better match
}

/**
 * Escape special regex characters in a string to treat them as literals.
 * This prevents regex syntax errors when user input contains metacharacters.
 */
const escapeRegex = (str: string): string => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

/**
 * Score a single token against a target string.
 * Lower scores indicate better matches.
 */
const scoreToken = (
  token: string,
  target: string,
  isMakeField: boolean
): number => {
  const lowerTarget = target.toLowerCase();
  const lowerToken = token.toLowerCase();

  // Exact match (entire field matches token)
  if (lowerTarget === lowerToken) {
    return isMakeField ? 0 : 1; // Prefer make matches
  }

  // Prefix match (token starts the field)
  if (lowerTarget.startsWith(lowerToken)) {
    return isMakeField ? 10 : 11;
  }

  // Word boundary match (token starts a word in the field)
  const wordBoundaryRegex = new RegExp(`\\b${escapeRegex(lowerToken)}`, "i");
  if (wordBoundaryRegex.test(lowerTarget)) {
    const matchIndex = lowerTarget.search(wordBoundaryRegex);
    return isMakeField ? 20 + matchIndex : 21 + matchIndex;
  }

  // Substring match (token appears anywhere)
  const substringIndex = lowerTarget.indexOf(lowerToken);
  if (substringIndex !== -1) {
    return isMakeField ? 30 + substringIndex : 31 + substringIndex;
  }

  // Fuzzy match (Levenshtein distance) - only for tokens of 4+ characters
  // Short tokens should rely on exact/prefix/substring matching
  if (lowerToken.length >= 4) {
    const distance = levenshtein(lowerToken, lowerTarget);
    // Use stricter threshold for shorter tokens
    const threshold = lowerToken.length <= 5 ? 2 : 4;
    if (distance <= threshold) {
      return isMakeField ? 40 + distance : 41 + distance;
    }
  }

  // No match
  return Infinity;
};

/**
 * Calculate the overall score for an aircraft against the search input.
 * Uses token-based scoring with partial matching support.
 */
const scoreAircraft = (aircraft: Aircraft, inputTokens: string[]): number => {
  const make = aircraft.make || "";
  const model = aircraft.model || "";
  const fullName = `${make} ${model}`;

  // Score each token independently and sum them
  const tokenScores = inputTokens.map((token) => {
    // Try matching against make, model, and full name
    const makeScore = scoreToken(token, make, true);
    const modelScore = scoreToken(token, model, false);
    const fullNameScore = scoreToken(token, fullName, false);

    // Return the best (lowest) score for this token
    return Math.min(makeScore, modelScore, fullNameScore);
  });

  // Sum all token scores (allows partial matching)
  // If any token has Infinity score, the total will be Infinity
  const totalScore = tokenScores.reduce((sum, score) => sum + score, 0);

  return totalScore;
};

// Memoized filtering function to improve performance
export const createFilteredOptions = (
  options: Aircraft[],
  inputValue: string
): Aircraft[] => {
  if (!inputValue) return options;

  // Prepare input tokens
  const input = inputValue.toLowerCase().trim();
  const inputTokens = input.split(/\s+/).filter((t) => t.length > 0);

  if (inputTokens.length === 0) return options;

  // Calculate scores for all aircraft
  const scoredAircraft = options.map((option): FilteredAircraft => {
    const score = scoreAircraft(option, inputTokens);
    return { option, score };
  });

  // Filter out aircraft with no matches (Infinity score)
  const filteredMatches = scoredAircraft.filter(
    (match) => match.score !== Infinity
  );

  // Sort by score (ascending - lower is better)
  filteredMatches.sort((a, b) => a.score - b.score);

  // Return just the aircraft objects in sorted order
  return filteredMatches.map((match) => match.option);
};

// Hook that provides memoized filtering with debouncing support
export const useFilteredAircraftOptions = (
  options: Aircraft[],
  inputValue: string
) => {
  return useMemo(() => {
    return createFilteredOptions(options, inputValue);
  }, [options, inputValue]);
};

// Performance-optimized filterOptions function for Autocomplete
export const filterOptions = (
  options: Aircraft[],
  { inputValue }: { inputValue: string }
): Aircraft[] => {
  return createFilteredOptions(options, inputValue);
};

/**
 * Result of finding the best matching aircraft for a type hint
 */
export interface BestMatchResult {
  /** The best matching aircraft, or null if no good match */
  aircraft: Aircraft | null;
  /** True if the match is strong enough to auto-select (score <= threshold) */
  isExactMatch: boolean;
  /** The match score (lower is better, Infinity means no match) */
  score: number;
}

/**
 * Find the best matching aircraft for a given type hint string.
 * Used to pre-select aircraft in dropdowns when we have a type hint from FlightBridge.
 *
 * @param typeHint - The aircraft type string (e.g., "Pilatus PC-24" from FlightBridge)
 * @param options - The list of aircraft to search
 * @param exactMatchThreshold - Score at or below which we consider it an "exact" match (default: 5)
 * @returns The best match result with aircraft, confidence flag, and score
 */
export const findBestMatch = (
  typeHint: string,
  options: Aircraft[],
  exactMatchThreshold = 5
): BestMatchResult => {
  if (!typeHint || !options || options.length === 0) {
    return { aircraft: null, isExactMatch: false, score: Infinity };
  }

  // Prepare input tokens (same as createFilteredOptions)
  const input = typeHint.toLowerCase().trim();
  const inputTokens = input.split(/\s+/).filter((t) => t.length > 0);

  if (inputTokens.length === 0) {
    return { aircraft: null, isExactMatch: false, score: Infinity };
  }

  // Find the best match
  let bestMatch: Aircraft | null = null;
  let bestScore = Infinity;

  for (const option of options) {
    const score = scoreAircraft(option, inputTokens);
    if (score < bestScore) {
      bestScore = score;
      bestMatch = option;
    }
  }

  return {
    aircraft: bestMatch,
    isExactMatch: bestScore <= exactMatchThreshold,
    score: bestScore,
  };
};
